import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/Forms';
import SafeIcon from '../../common/SafeIcon';
import supabase from '../../supabase/supabase';
import { motion } from 'framer-motion';

export default function DiagnosticPanel({ profile }) {
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [msg, setMsg] = useState({ type: '', text: '', debug: '' });

  useEffect(() => {
    fetchHistory();
    const channel = supabase.channel('test_emails_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'test_emails_20240523' }, () => fetchHistory())
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, []);

  const fetchHistory = async () => {
    const { data } = await supabase.from('test_emails_20240523').select('*').order('created_at', { ascending: false }).limit(6);
    setHistory(data || []);
  };

  const runDiagnostic = async (method) => {
    setLoading(true);
    setMsg({ type: 'info', text: `Initiating ${method} sequence...`, debug: '' });
    try {
      if (method === 'Webhook') {
        const { error } = await supabase.from('test_emails_20240523').insert([{
          user_id: profile.id, to_email: profile.email, subject: 'SubSift: Webhook Test', body_text: 'DB Trigger Test', status: 'pending'
        }]);
        if (error) throw error;
        setMsg({ type: 'info', text: 'Diagnostic Logged', debug: 'Waiting for database trigger processing...' });
      } else if (method === 'Ping') {
        const { data, error } = await supabase.functions.invoke('send-test-email', { body: { ping: true } });
        if (error) throw error;
        setMsg({ type: 'success', text: 'Gateway Reachable', debug: 'Edge Function is online and healthy.' });
      } else {
        const { error } = await supabase.functions.invoke('send-test-email', {
          body: { to_email: profile.email, subject: 'SubSift: Manual Test', body_text: 'Direct Invoke' }
        });
        if (error) {
          const body = await error.context?.json().catch(() => ({}));
          throw new Error(body?.error || error.message);
        }
        setMsg({ type: 'success', text: 'Transmission Successful', debug: 'Email payload delivered to SMTP server.' });
      }
    } catch (err) {
      setMsg({ type: 'error', text: 'Transmission Failed', debug: err.message });
    } finally {
      setLoading(false);
      fetchHistory();
    }
  };

  return (
    <div className="bg-white p-8 rounded-[32px] shadow-sm border border-slate-100">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 mb-8">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-rose-50 text-rose-600 rounded-2xl">
            <SafeIcon name="Activity" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-800">Diagnostics</h2>
            <p className="text-xs text-slate-500 font-medium">Real-time delivery verification</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" onClick={() => runDiagnostic('Ping')} disabled={loading} className="text-[11px] px-4 py-2 border-slate-200">Ping Gateway</Button>
          <Button onClick={() => runDiagnostic('Webhook')} disabled={loading} className="text-[11px] px-4 py-2">Full Cycle Test</Button>
        </div>
      </div>

      {msg.text && (
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className={`p-5 rounded-2xl mb-8 border ${
          msg.type === 'error' ? 'bg-red-50 text-red-600 border-red-100' : 
          msg.type === 'success' ? 'bg-teal-50 text-teal-600 border-teal-100' : 
          'bg-blue-50 text-blue-600 border-blue-100'
        }`}>
          <div className="flex items-start gap-3">
            <SafeIcon name={msg.type === 'error' ? 'AlertCircle' : msg.type === 'success' ? 'CheckCircle' : 'Info'} className="w-5 h-5 mt-0.5" />
            <div>
              <p className="font-bold text-sm mb-1">{msg.text}</p>
              <p className="text-xs opacity-80 font-mono break-all">{msg.debug}</p>
            </div>
          </div>
        </motion.div>
      )}

      <div className="space-y-3">
        <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest pl-1">Recent Transmissions</h3>
        <div className="rounded-2xl border border-slate-50 overflow-hidden">
          <table className="w-full text-left">
            <thead className="bg-slate-50/50 text-slate-400 text-[10px] uppercase font-bold">
              <tr>
                <th className="p-4">Time</th>
                <th className="p-4">Status</th>
                <th className="p-4">Detailed Result</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {history.map(h => (
                <tr key={h.id} className="hover:bg-slate-50/30 transition-colors">
                  <td className="p-4 text-xs text-slate-500">{new Date(h.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</td>
                  <td className="p-4">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase ${
                      h.status === 'sent' ? 'bg-teal-50 text-teal-600' : 
                      h.status === 'failed' ? 'bg-red-50 text-red-600' : 
                      'bg-amber-50 text-amber-600'
                    }`}>
                      <div className={`w-1.5 h-1.5 rounded-full ${h.status === 'sent' ? 'bg-teal-500' : h.status === 'failed' ? 'bg-red-500' : 'bg-amber-500'}`} />
                      {h.status}
                    </span>
                  </td>
                  <td className="p-4 text-xs text-slate-400 truncate max-w-[200px]" title={h.error_log}>
                    {h.error_log || 'Transmission successful'}
                  </td>
                </tr>
              ))}
              {history.length === 0 && (
                <tr>
                  <td colSpan="3" className="p-8 text-center text-slate-300 text-sm italic">No recent logs found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}